import { createSignal, type JSX } from 'solid-js';
import { MyKeysView } from '../components/MyKeysView';
import { t } from '../i18n';
import { notifyKeyCapture } from '../lib/key-capture';
import { useMyKeyPriority } from '../lib/key-priority';
import { keyVerifyNotice } from '../lib/key-verify';
import { createKeyedMutation } from '../lib/keyed-mutation';
import { createKeyRotate } from '../lib/keyRotate';
import type { AddKeyInput, KeyRow, ProbeModelsInput } from '../lib/keys';
import { createLlmPrefEditor } from '../lib/llmPrefEditor';
import { createMutation } from '../lib/mutation';
import {
  addMyKey,
  probeMyProviderModels,
  revokeMyKey,
  rotateMyKey,
  useMyKeys,
  verifyMyKey,
} from '../lib/my-keys';
import { clearMyLlmPref, setMyLlmPref, useMyLlmPref } from '../lib/my-llm-pref';
import { SESSION_TOKEN } from '../lib/session';
import { createToast } from '../lib/toast';

// Personal BYOK keys (epic #635) — the acting user's OWN provider keys within the
// active org, which rank AHEAD of the org's shared pool, plus a per-user
// default-model preference. This is the CONTAINER: all data-fetching + mutation
// plumbing; the JSX presenter is `MyKeysView` (SOLID: two files, two reasons to
// change). Mirrors `AccountKeys` but hits `/v1/me/*` and adds verify + the pref.

export function MyKeys(props: { token?: string | null } = {}): JSX.Element {
  const token = props.token ?? SESSION_TOKEN;
  const query = useMyKeys(token);
  let heading: HTMLHeadingElement | undefined;

  const [addOpen, setAddOpen] = createSignal(false);
  const [revoking, setRevoking] = createSignal<KeyRow | null>(null);

  const { toast, notify, dismiss } = createToast();

  // Rotate (#971) — `lib/keyRotate.ts` holds the 200/422 contract.
  const rotate = createKeyRotate({
    rotate: (id, body) => rotateMyKey(id, body, token),
    refetch: () => query.refetch(),
    notify,
  });

  // Re-rank one of the member's own keys (plan 102 slice 05b). Every member may:
  // `write:self:keys`, so no role gate.
  const priority = useMyKeyPriority(token, notify);

  // The resync runs on BOTH outcomes and inside the pending window: a refused add
  // may still have sealed a row, and the server's list is the only thing that
  // knows — so the dialog stays busy until the card list reflects it.
  const add = createMutation({
    call: (input: AddKeyInput) => addMyKey(input, token),
    fallback: t('account_keys.add_error'),
    onSuccess: (answer) => {
      setAddOpen(false);
      notifyKeyCapture(answer, notify, verify);
    },
    onSettled: () => query.refetch(),
  });

  // Re-verify a key against its provider. On 422 the row persists as `invalid`,
  // so we always refetch and let the card reflect server truth — and toast the
  // outcome either way, the refusal in the server's own words: a swallowed
  // failure repainted an identical card, a dead button (plan 101 slice 17 row
  // 17-15, the org page's #924 shape). Keyed per row, so a double click sends one.
  const verifying = createKeyedMutation({
    fallback: t('account_keys.verify_failed_toast'),
    onError: (message) => notify(message, 'error'),
  });
  const verify = (row: KeyRow): Promise<void> =>
    verifying.run(row.id, async () => {
      try {
        const notice = keyVerifyNotice(await verifyMyKey(row.id, token));
        notify(notice.message, notice.tone);
      } finally {
        await query.refetch();
      }
    });

  const revoke = createMutation({
    call: (id: string) => revokeMyKey(id, token),
    fallback: t('account_keys.revoke_error'),
    onSuccess: async () => {
      setRevoking(null);
      await query.refetch();
      queueMicrotask(() => {
        const active = document.activeElement;
        if (!active || active === document.body || !active.isConnected) heading?.focus();
      });
    },
    // Keep the confirm open + surface it — destructive actions never no-op silently.
    onError: (message) => notify(message, 'error'),
  });

  // One busy state for both writes, as when they shared a signal.
  const busy = (): boolean => add.pending() || revoke.pending();
  const addError = add.error;

  const submitAdd = (input: AddKeyInput): void => void add.run(input);

  const confirmRevoke = (): void => {
    const row = revoking();
    if (!row) return;
    void revoke.run(row.id);
  };

  // Per-user default-model preference (`/v1/me/llm-pref`) — `lib/llmPrefEditor.ts`
  // holds its busy/error/re-read contract, the `createKeyRotate` split again.
  const prefQuery = useMyLlmPref(token);
  const pref = createLlmPrefEditor({
    query: prefQuery,
    set: (input) => setMyLlmPref(input, token),
    clear: () => clearMyLlmPref(token),
  });

  return (
    <MyKeysView
      hasSession={token !== null}
      headingRef={(el) => {
        heading = el;
      }}
      keysQuery={query}
      addOpen={addOpen()}
      addBusy={busy()}
      addError={addError()}
      onOpenAdd={() => {
        add.clearError();
        setAddOpen(true);
      }}
      onSubmitAdd={submitAdd}
      onCloseAdd={() => setAddOpen(false)}
      onProbeModels={(input: ProbeModelsInput) => probeMyProviderModels(input, token)}
      onVerify={(row) => void verify(row)}
      onAskRevoke={setRevoking}
      revoking={revoking()}
      onCloseRevoke={() => setRevoking(null)}
      onConfirmRevoke={confirmRevoke}
      onAskRotate={rotate.open}
      rotating={rotate.rotating()}
      rotateBusy={rotate.busy()}
      rotateError={rotate.error()}
      onSubmitRotate={rotate.submit}
      onCloseRotate={rotate.close}
      onSetPriority={(row, value) => void priority.save(row, value)}
      priorityBusy={priority.isBusy}
      pref={pref.pref()}
      prefBusy={pref.busy()}
      prefError={pref.error()}
      onSavePref={pref.save}
      onClearPref={pref.clear}
      toast={toast()}
      onToastDismiss={dismiss}
    />
  );
}
